import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    Input,
    OnDestroy,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import {FormControlName, FormGroup} from '@angular/forms';
import {BOTTOM_POSITION} from '@common/core/ui/overlay-panel/positions/bottom-position';
import {OverlayPanelRef} from '@common/core/ui/overlay-panel/overlay-panel-ref';
import {OverlayPanel} from '@common/core/ui/overlay-panel/overlay-panel.service';
import {
    UpgradeOverlayComponent,
    UpgradeOverlayData,
} from '../permissions/upgrade-overlay/upgrade-overlay.component';
import {Settings} from '@common/core/config/settings.service';
import {CurrentUser} from '@common/auth/current-user';
import {LinkUsageService} from '../../shared/link/link-usage.service';
import {LinkFeaturePermissions} from '../../shared/link/link-usage-response';
import {BehaviorSubject, Subscription} from 'rxjs';

@Component({
    selector: '[permission-aware-input-container]',
    templateUrl: './permission-aware-input-container.component.html',
    styleUrls: ['./permission-aware-input-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionAwareInputContainerComponent
    implements AfterViewInit, OnDestroy
{
    @Input('permission-aware-input-container')
    permission: keyof LinkFeaturePermissions;
    @Input() controlGroup: FormGroup;

    @ContentChild(FormControlName) controlName: FormControlName;
    @ViewChild('lockIcon', {read: ElementRef})
    lockIcon: ElementRef<HTMLElement>;
    private listeningToHover: boolean;
    private overlayRef: OverlayPanelRef<UpgradeOverlayComponent>;
    lockIconHidden$ = new BehaviorSubject<boolean>(true);
    private usageSub: Subscription;

    constructor(
        private cd: ChangeDetectorRef,
        private overlay: OverlayPanel,
        private viewContainerRef: ViewContainerRef,
        private settings: Settings,
        private currentUser: CurrentUser,
        private usage: LinkUsageService
    ) {}

    ngAfterViewInit() {
        setTimeout(() => {
            this.usageSub = this.usage.response$.subscribe(response => {
                if (
                    !response.usage.links[this.permission] &&
                    !this.currentUser.isAdmin()
                ) {
                    this.lockIconHidden$.next(false);

                    // disable multiple form controls
                    if (this.controlGroup) {
                        this.controlGroup.disable();
                    }

                    // disable single form control
                    else if (this.controlName) {
                        this.controlName.control.disable();
                    }

                    if (this.lockIcon) {
                        this.createHoverListener();
                    }
                }
            });
        });
    }

    ngOnDestroy() {
        this.usageSub?.unsubscribe();
    }

    private createHoverListener() {
        if (!this.listeningToHover) {
            this.lockIcon.nativeElement.addEventListener('mouseenter', () => {
                this.cd.markForCheck();
                this.showMessage();
            });
            this.lockIcon.nativeElement.addEventListener('mouseleave', e => {
                const toElement = e.relatedTarget as HTMLElement;
                if (toElement?.nodeName?.toLowerCase() !== 'upgrade-overlay') {
                    this.hideMessage();
                }
            });
            this.listeningToHover = true;
        }
    }

    showMessage() {
        if (this.overlayRef) {
            this.hideMessage();
        }
        this.overlayRef = this.overlay.open(UpgradeOverlayComponent, {
            origin: this.lockIcon,
            position: BOTTOM_POSITION,
            hasBackdrop: false,
            scrollStrategy: 'close',
            viewContainerRef: this.viewContainerRef,
            data: {
                config: {resource: 'links', createMsgType: this.permission},
            } as UpgradeOverlayData,
        });
        this.overlayRef.getPanelEl().addEventListener('mouseleave', () => {
            this.hideMessage();
        });
    }

    public hideMessage() {
        if (this.overlayRef) {
            this.overlayRef.close();
            this.overlayRef = null;
        }
    }
}
